import { Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { Share06Icon } from '@/components/icons/Share06Icon';
import { EmptyListActionCard } from '@/components/lists/EmptyListActionCard';
import {
  ButtonTab,
  TabContent,
  Tabs,
} from '@/components/navigation/NavigationTabs';
import { TabsProvider } from '@/components/navigation/NavigationTabs/Tabs.provider';
import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { useModalState } from '@/hooks/useModalState';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { EstateWaterfallHypotheticalCharitableTrustModal } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalCharitableTrustModal/EstateWaterfallHypotheticalCharitableTrustModal';
import { HypotheticalCharitableTrustModalKind } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalCharitableTrustModal/EstateWaterfallHypotheticalCharitableTrustModal.types';
import { EstateWaterfallHypotheticalGratModal } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalGratModal/EstateWaterfallHypotheticalGratModal';
import { EstateWaterfallHypotheticalSaleLoanModal } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalSaleLoanModal/EstateWaterfallHypotheticalSaleLoanModal';
import { HypotheticalGratNotificationCallout } from '@/modules/estateWaterfall/components/HypotheticalGratNotificationCallout/HypotheticalGratNotificationCallout';
import { getNewHypotheticalItemNotification } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { EstateWaterfall_ProposalFragment } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { ClientProfileSummaryPanel_ClientProfileFragment } from '@/modules/summaryPanels/ClientProfileSummaryPanel/graphql/ClientProfileSummaryPanel.generated';
import { CreateHypotheticalEventButton } from '@/modules/summaryPanels/HypotheticalTransfersSummaryPanel/CreateHypotheticalEventButton';
import { OrganizationSummaryPanel_OrganizationFragment } from '@/modules/summaryPanels/OrganizationSummaryPanel/graphql/OrganizationSummaryPanel.generated';

import { useWaterfallRouteContext } from '../../contexts/waterfallRoute/waterfallRoute.context';
import { EntityDetail_EntityFragment } from '../graphql/EntityDetailPage.generated';
import { HypotheticalILITModal } from '../HypotheticalILITModal/HypotheticalILITModal';
import { useGetHypotheticalTransfers } from './hooks/useGetHypotheticalTransfers';
import { useTransfersCardHypotheticalTransfers } from './hooks/useTransfersCardHypotheticalTransfers';
import { StrategyItemsTable } from './StrategyItemsTable';
import { TransferItems, TransferTab } from './TransferItems';

interface HypotheticalTransfersCardInnerViewState {
  showNoTransfersMessage: boolean;
  showTransfers: boolean;
}

export interface OnEditStrategyTransferInput {
  kind: 'saleLoan' | 'grat' | 'crt' | 'clt' | 'insurance';
  strategyId: string;
}

interface HypotheticalTransfersCardInnerProps {
  transferReferenceObj:
    | EntityDetail_EntityFragment
    | ClientProfileSummaryPanel_ClientProfileFragment
    | EstateWaterfall_ProposalFragment
    | OrganizationSummaryPanel_OrganizationFragment;
  waterfallId: string;
  householdId: string;
  entityId: string;
  setShowLoader: (showLoader: boolean) => void;
}

function HypotheticalTransfersCardInner({
  transferReferenceObj,
  waterfallId,
  householdId,
  entityId,
  setShowLoader,
}: HypotheticalTransfersCardInnerProps) {
  const canModifyHypotheticalTransfers = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MODIFY_HYPOTHETICAL_TRANSFERS
  );
  const [
    { isModalOpen: isSaleLoanModalOpen, data: saleLoanId },
    { closeModal: closeSaleLoanModal, openModal: openSaleLoanModal },
  ] = useModalState<string>();

  const [
    { isModalOpen: isGratModalOpen, data: gratId },
    { closeModal: closeGratModal, openModal: openGratModal },
  ] = useModalState<string>();

  const [
    { isModalOpen: isCrtModalOpen, data: crtId },
    { closeModal: closeCrtModal, openModal: openCrtModal },
  ] = useModalState<string>();

  const [
    { isModalOpen: isCltModalOpen, data: cltId },
    { closeModal: closeCltModal, openModal: openCltModal },
  ] = useModalState<string>();

  const [
    { isModalOpen: isInsuranceModalOpen, data: insuranceId },
    { closeModal: closeInsuranceModal, openModal: openInsuranceModal },
  ] = useModalState<string>();

  const { activeTab, setActiveTab } = useActiveTab<TransferTab>({
    defaultTab: TransferTab.Outbound,
    filterSearchParam: 'transfer-direction',
  });

  const isEntity = transferReferenceObj.__typename === 'Entity';
  const isIndividual = transferReferenceObj.__typename === 'ClientProfile';
  const isProposal = transferReferenceObj.__typename === 'Proposal';

  // Fetch outbound transfers
  const {
    loading,
    // Transfers
    outboundTransfers,
    inboundTransfers,
    // Loans
    sourceLoans,
    recipientLoans,
    // Grats
    sourceHypotheticalGrats,
    recipientHypotheticalGrats,
    // Grats Annuities
    inboundGratAnnuities,
    outboundGratAnnuities,
    // Grats Beneficiary Payments
    inboundGratBeneficiaryPayments,
    outboundGratBeneficiaryPayments,
    // Crts
    sourceHypotheticalCrts,
    recipientHypotheticalCrts,
    // Crts Income Payments
    inboundHypotheticalCrtIncomePayments,
    outboundHypotheticalCrtIncomePayments,
    // Crts Beneficiary Payments
    inboundHypotheticalCrtBeneficiaryPayments,
    outboundHypotheticalCrtBeneficiaryPayments,
    // Clts
    sourceHypotheticalClts,
    recipientHypotheticalClts,
    // Clts Income Payments
    inboundHypotheticalCltIncomePayments,
    outboundHypotheticalCltIncomePayments,
    // Clts Beneficiary Payments
    inboundHypotheticalCltBeneficiaryPayments,
    outboundHypotheticalCltBeneficiaryPayments,

    // QPRT Beneficiary Payments
    inboundQprtBeneficiaryPayments,
    outboundQprtBeneficiaryPayments,

    // Insurance
    sourceHypotheticalInsurance,
    recipientHypotheticalInsurance,

    refetch,
    transferLog,
  } = useGetHypotheticalTransfers({
    transferReferenceObjId: transferReferenceObj.id,
    waterfallId,
    isEntity,
    isIndividual,
    isProposal,
  });

  // Process transfers data
  const {
    hasTransfers,
    hasSaleLoans,
    hasHypotheticalGrats,
    hasHypotheticalCrts,
    hasHypotheticalClts,
    hasHypotheticalInsurance,
    hasGratAnnuities,
    hasGratBeneficiaryPayments,
    hasQprtBeneficiaryPayments,
    hasHypotheticalCrtIncomePayments,
    hasHypotheticalCrtBeneficiaryPayments,
    hasHypotheticalCltIncomePayments,
    hasHypotheticalCltBeneficiaryPayments,
    inboundCount,
    outboundCount,
    transfers,
    saleLoans,
    hypotheticalGrats,
    hypotheticalCrts,
    hypotheticalClts,
    hypotheticalInsurance,
  } = useTransfersCardHypotheticalTransfers({
    inboundTransfers,
    outboundTransfers,
    sourceLoans,
    recipientLoans,
    sourceHypotheticalGrats,
    recipientHypotheticalGrats,
    inboundGratAnnuities,
    outboundGratAnnuities,
    inboundGratBeneficiaryPayments,
    outboundGratBeneficiaryPayments,
    inboundQprtBeneficiaryPayments,
    outboundQprtBeneficiaryPayments,
    sourceHypotheticalCrts,
    recipientHypotheticalCrts,
    inboundHypotheticalCrtIncomePayments,
    outboundHypotheticalCrtIncomePayments,
    inboundHypotheticalCrtBeneficiaryPayments,
    outboundHypotheticalCrtBeneficiaryPayments,
    sourceHypotheticalClts,
    recipientHypotheticalClts,
    inboundHypotheticalCltIncomePayments,
    outboundHypotheticalCltIncomePayments,
    inboundHypotheticalCltBeneficiaryPayments,
    outboundHypotheticalCltBeneficiaryPayments,
    sourceHypotheticalInsurance,
    recipientHypotheticalInsurance,
  });

  const refetchTransfers = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleCloseModal = useCallback(() => {
    // Only one modal can be open at a time
    // so it is okay to close both
    closeSaleLoanModal();
    closeGratModal();
    closeCrtModal();
    closeCltModal();
    closeInsuranceModal();
    void refetchTransfers();
  }, [
    closeSaleLoanModal,
    closeGratModal,
    closeCrtModal,
    closeCltModal,
    closeInsuranceModal,
    refetchTransfers,
  ]);

  const handleEditStrategyTransfer = useCallback(
    ({ kind, strategyId }: OnEditStrategyTransferInput) => {
      if (kind === 'saleLoan') {
        openSaleLoanModal(strategyId);
      } else if (kind === 'grat') {
        openGratModal(strategyId);
      } else if (kind === 'crt') {
        openCrtModal(strategyId);
      } else if (kind === 'clt') {
        openCltModal(strategyId);
      } else if (kind === 'insurance') {
        openInsuranceModal(strategyId);
      }
    },
    [
      openSaleLoanModal,
      openGratModal,
      openCrtModal,
      openCltModal,
      openInsuranceModal,
    ]
  );

  useEffect(() => {
    // Set parent loader state
    setShowLoader(loading);
  }, [loading, setShowLoader]);

  // Determine view state
  const hasAnyTransfers =
    hasTransfers ||
    hasSaleLoans ||
    hasHypotheticalGrats ||
    hasHypotheticalCrts ||
    hasHypotheticalClts ||
    hasHypotheticalInsurance ||
    hasGratAnnuities ||
    hasGratBeneficiaryPayments ||
    hasQprtBeneficiaryPayments ||
    hasHypotheticalCrtIncomePayments ||
    hasHypotheticalCrtBeneficiaryPayments ||
    hasHypotheticalCltIncomePayments ||
    hasHypotheticalCltBeneficiaryPayments;

  const viewState: HypotheticalTransfersCardInnerViewState = {
    showNoTransfersMessage: !hasAnyTransfers,
    showTransfers: !!hasAnyTransfers,
  };

  // Get the notification data for the hypothetical item
  const hypotheticalItemNotification = getNewHypotheticalItemNotification();

  const createTransferButton = canModifyHypotheticalTransfers ? (
    <CreateHypotheticalEventButton
      onCloseSubmodal={refetchTransfers}
      waterfallId={waterfallId}
      householdId={householdId}
      initialSourceEntityId={
        isEntity && activeTab === TransferTab.Outbound ? entityId : undefined
      }
      variant={viewState.showNoTransfersMessage ? 'primary' : 'secondary'}
      disabled={!transferReferenceObj}
      size="sm"
      fullWidth={!viewState.showNoTransfersMessage}
    />
  ) : undefined;

  return (
    <>
      {isSaleLoanModalOpen && saleLoanId && (
        <EstateWaterfallHypotheticalSaleLoanModal
          isOpen={isSaleLoanModalOpen}
          onClose={handleCloseModal}
          saleLoanId={saleLoanId}
          waterfallId={waterfallId}
          householdId={householdId}
          initialKind={null}
        />
      )}
      {isGratModalOpen && gratId && (
        <EstateWaterfallHypotheticalGratModal
          isOpen={isGratModalOpen}
          onClose={handleCloseModal}
          gratId={gratId}
          waterfallId={waterfallId}
          householdId={householdId}
        />
      )}
      {(isCrtModalOpen || isCltModalOpen) && (
        <EstateWaterfallHypotheticalCharitableTrustModal
          waterfallId={waterfallId}
          isOpen={isCrtModalOpen || isCltModalOpen}
          onClose={handleCloseModal}
          hcrtId={crtId}
          hcltId={cltId}
          householdId={householdId}
          charitableTrustModalKind={
            isCrtModalOpen
              ? HypotheticalCharitableTrustModalKind.CRT
              : HypotheticalCharitableTrustModalKind.CLT
          }
        />
      )}
      {isInsuranceModalOpen && insuranceId && (
        <HypotheticalILITModal
          onClose={handleCloseModal}
          waterfallId={waterfallId}
          householdId={householdId}
          hilitId={insuranceId}
        />
      )}
      <Stack p={viewState.showTransfers ? 0 : 3} py={0}>
        {/* Empty state */}
        {viewState.showNoTransfersMessage && (
          <EmptyListActionCard
            icon={Share06Icon}
            heading="Specify transfers"
            description="No transfers have been created for this entity"
            action={createTransferButton}
            hideButton={!canModifyHypotheticalTransfers}
          />
        )}
        <Stack display={viewState.showTransfers ? undefined : 'none'} pb={3}>
          <TabsProvider currentTab={activeTab}>
            <Stack spacing={2}>
              <Tabs fullWidth>
                <ButtonTab
                  display="Outbound"
                  isActive={activeTab === TransferTab.Outbound}
                  onClick={() => setActiveTab(TransferTab.Outbound)}
                  badge={outboundCount.toString()}
                />
                <ButtonTab
                  display="Inbound"
                  isActive={activeTab === TransferTab.Inbound}
                  onClick={() => setActiveTab(TransferTab.Inbound)}
                  badge={inboundCount.toString()}
                />
              </Tabs>
              {hypotheticalItemNotification &&
                !hypotheticalItemNotification.dismissed && (
                  <HypotheticalGratNotificationCallout
                    entityId={
                      'entityId' in hypotheticalItemNotification
                        ? hypotheticalItemNotification.entityId
                        : undefined
                    }
                    proposalId={
                      'proposalId' in hypotheticalItemNotification
                        ? hypotheticalItemNotification.proposalId
                        : undefined
                    }
                  />
                )}
              <TabContent tabKey={TransferTab.Outbound}>
                <Stack spacing={2}>
                  <TransferItems
                    transfers={transfers}
                    activeTab={TransferTab.Outbound}
                    transferReferenceObj={transferReferenceObj}
                    waterfallId={waterfallId}
                    householdId={householdId}
                    entityId={entityId}
                  />
                  <StrategyItemsTable
                    saleLoansWithProjections={saleLoans}
                    grats={hypotheticalGrats}
                    gratAnnuities={outboundGratAnnuities}
                    gratBeneficiaryPayments={outboundGratBeneficiaryPayments}
                    qprtBeneficiaryPayments={outboundQprtBeneficiaryPayments}
                    hypotheticalCrts={hypotheticalCrts}
                    hypotheticalCrtIncomePayments={
                      outboundHypotheticalCrtIncomePayments
                    }
                    hypotheticalCrtBeneficiaryPayments={
                      outboundHypotheticalCrtBeneficiaryPayments
                    }
                    hypotheticalClts={hypotheticalClts}
                    hypotheticalCltIncomePayments={
                      outboundHypotheticalCltIncomePayments
                    }
                    hypotheticalCltBeneficiaryPayments={
                      outboundHypotheticalCltBeneficiaryPayments
                    }
                    hypotheticalInsurance={hypotheticalInsurance}
                    direction={TransferTab.Outbound}
                    transferLog={transferLog}
                    onEditStrategyTransfer={handleEditStrategyTransfer}
                  />
                </Stack>
              </TabContent>
              <TabContent tabKey={TransferTab.Inbound}>
                <Stack spacing={2}>
                  <TransferItems
                    transfers={transfers}
                    activeTab={TransferTab.Inbound}
                    transferReferenceObj={transferReferenceObj}
                    waterfallId={waterfallId}
                    householdId={householdId}
                    entityId={entityId}
                  />
                  <StrategyItemsTable
                    saleLoansWithProjections={saleLoans}
                    grats={hypotheticalGrats}
                    gratAnnuities={inboundGratAnnuities}
                    gratBeneficiaryPayments={inboundGratBeneficiaryPayments}
                    qprtBeneficiaryPayments={inboundQprtBeneficiaryPayments}
                    hypotheticalCrts={hypotheticalCrts}
                    hypotheticalCrtIncomePayments={
                      inboundHypotheticalCrtIncomePayments
                    }
                    hypotheticalCrtBeneficiaryPayments={
                      inboundHypotheticalCrtBeneficiaryPayments
                    }
                    hypotheticalClts={hypotheticalClts}
                    hypotheticalCltIncomePayments={
                      inboundHypotheticalCltIncomePayments
                    }
                    hypotheticalCltBeneficiaryPayments={
                      inboundHypotheticalCltBeneficiaryPayments
                    }
                    hypotheticalInsurance={hypotheticalInsurance}
                    direction={TransferTab.Inbound}
                    transferLog={transferLog}
                    onEditStrategyTransfer={handleEditStrategyTransfer}
                  />
                </Stack>
              </TabContent>
            </Stack>
          </TabsProvider>
        </Stack>
        {viewState.showTransfers && createTransferButton}
      </Stack>
    </>
  );
}

interface HypotheticalTransfersCardProps {
  entity?: EntityDetail_EntityFragment;
  individual?: ClientProfileSummaryPanel_ClientProfileFragment;
  proposal?: EstateWaterfall_ProposalFragment;
  organization?: OrganizationSummaryPanel_OrganizationFragment;
}

export function HypotheticalTransfersCard({
  entity,
  individual,
  proposal,
  organization,
}: HypotheticalTransfersCardProps) {
  const { waterfallId } = useWaterfallRouteContext();

  const [showLoader, setShowLoader] = useState(false);

  const transferReferenceObj = entity ?? individual ?? proposal ?? organization;

  if (!waterfallId || !transferReferenceObj) {
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay open={showLoader} updatingText="Loading..." />
      <HypotheticalTransfersCardInner
        key={entity?.id ?? individual?.id ?? ''}
        transferReferenceObj={transferReferenceObj}
        waterfallId={waterfallId}
        householdId={transferReferenceObj?.household?.id ?? ''}
        entityId={entity?.id ?? individual?.id ?? ''}
        setShowLoader={setShowLoader}
      />
    </div>
  );
}
